/* eslint-disable react/jsx-key */
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { SectionTypes } from '@common/enums/section-type-enum';
import { useCardStep } from '@common/helpers';
import { Section, SectionReferences } from '@common/models/section-model';
import { CardItem } from '@core/components';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { DrawingShapeContext } from '@ee-context';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import { CookingAppliance } from '../CookingAppliancesSection/CookingAppliancesSection';
import Cutouts from '../CutoutsSection/CutoutsSection';
import { PlumbingSection } from '../Plumbing/PlumbingSection';
import { CornerRadius } from '../corner-radius/corner-radius';
import CustomSide from '../custom-side/custom-side';
import DiagonalCorners from '../diagonal-corners/diagonal-corners';
import Dishwasher from '../dishwashers/dishwasher';
import { Edges } from '../edges/edges';
import Faucets from '../faucets/faucets';
import { Refrigerator } from '../refrigerators/refrigerator';
import Sink from '../sinks/sink';
import { TearOutCountertop } from '../tear-out-countertop/tear-out-countertop';

interface SectionLookupProps {
    section: SectionTypes;
    hideIfNoZipProp: boolean;
    zipcodeProp: boolean;
}

//Given a SectionType return the contents of each sections respective function call
const SectionLookup = ({ section, hideIfNoZipProp, zipcodeProp }: SectionLookupProps): JSX.Element => {
    if (hideIfNoZipProp && !zipcodeProp) {
        return <></>;
    }
    switch (section) {
        case SectionTypes.edge:
            return <Edges />;
        case SectionTypes.corner_radius:
            return <CornerRadius />;
        case SectionTypes.diagonal_corners:
            return <DiagonalCorners />;
        case SectionTypes.custom_side:
            return <CustomSide />;
        case SectionTypes.cooking_appliance:
            return <CookingAppliance />;
        case SectionTypes.dishwasher:
            return <Dishwasher />;
        case SectionTypes.sink:
            return <Sink />;
        case SectionTypes.refrigerator:
            return <Refrigerator />;
        case SectionTypes.tear_out_countertop:
            return <TearOutCountertop />;
        case SectionTypes.cutouts:
            return <Cutouts />;
        case SectionTypes.faucet:
            return <Faucets />;
        case SectionTypes.plumbing:
            return <PlumbingSection />;
        default:
            return <></>;
    }
};

const sectionReferenceLookup = (
    section: SectionTypes,
    sectionReference: SectionReferences,
    hideIfNoZipProp: boolean,
    zipcodeProp: boolean
) => {
    if (hideIfNoZipProp && !zipcodeProp) {
        return null;
    }
    switch (section) {
        case SectionTypes.edge:
            return sectionReference.edgeRef;
        case SectionTypes.corner_radius:
            return sectionReference.cornerRadiusRef;
        case SectionTypes.diagonal_corners:
            return sectionReference.diagonal_cornersRef;
        case SectionTypes.custom_side:
            return sectionReference.custom_sideRef;
        case SectionTypes.cooking_appliance:
            return sectionReference.cooking_applianceRef;
        case SectionTypes.dishwasher:
            return sectionReference.dishwasherRef;
        case SectionTypes.sink:
            return sectionReference.sinkRef;
        case SectionTypes.refrigerator:
            return sectionReference.refrigeratorRef;
        case SectionTypes.tear_out_countertop:
            return sectionReference.tear_out_countertopRef;
        case SectionTypes.cutouts:
            return sectionReference.cutoutsRef;
        case SectionTypes.faucet:
            return sectionReference.faucet;
        case SectionTypes.plumbing:
            return sectionReference.plumbing;
        default:
            return null;
    }
};

interface BottomSectionProps {
    sectionRef: SectionReferences;
    section: Section;
    totalSteps: number;
}

const BottomSection = ({ sectionRef, section, totalSteps }: BottomSectionProps): JSX.Element => {
    const { t } = useTranslation();
    const { getCardStep } = useCardStep();
    const zipcode = useAppSelector(selectZipcode);
    const { selectedValue: selectedColor } = useSelectedColor();
    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);

    //display a CardItem with a child that contains each of the SubSections within the given Section
    return (
        <CardItem
            id={section.stepNumber}
            title={t(section.name)}
            stepInfo={getCardStep(section.stepNumber, totalSteps)}
            description={''}
            disabled={!areThereShapesInTheDrawingArea || !selectedColor}
        >
            <div id={section.name}>
                {section.subSections.map((e, index) => (
                    <div key={index} ref={sectionReferenceLookup(e, sectionRef, section.hideIfNoZip, !!zipcode)}>
                        <SectionLookup hideIfNoZipProp={section.hideIfNoZip} section={e} zipcodeProp={!!zipcode} />
                    </div>
                ))}
            </div>
        </CardItem>
    );
};

export default BottomSection;
